"use client";

import { useEffect, useRef } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import { getActiveProfileId } from "@/helpers/jwt.util";
import { useContentImpressionFlush } from "@/hooks/useMediaAnalytics";
import { useAppSelector } from "@/redux/hooks";
import { flushContentImpressions } from "@/services/mediaAnalyticsService";
import {
  getScreenNameFromPath,
  isTvUserAgent,
  trackGtagEvent,
} from "@/services/gtagAnalyticsService";
import {
  authorizeMetrixUser,
  buildMetrixCustomUserId,
  deauthorizeMetrixUser,
  initMetrix,
} from "@/services/metrixAnalyticsService";

const GOOGLE_ANALYTICS_ID = "G-KKBKQWG4Z7";
const isProduction = process.env.NODE_ENV === "production";

const AnalyticsClient = () => {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const isAdminPreview = pathname === "/adm" || pathname.startsWith("/adm/");
  const authProfile = useAppSelector((state) => state.Auth?.profile || {});
  const hasTrackedInitialPageView = useRef(false);
  const lastMetrixUserId = useRef(null);
  const activeProfileId =
    getActiveProfileId() || authProfile?._id || authProfile?.profileId || "";
  const metrixUserId = buildMetrixCustomUserId(
    authProfile?.phoneNumber || authProfile?.mobileNumber,
    activeProfileId,
  );

  useContentImpressionFlush();

  useEffect(() => {
    initMetrix();
  }, []);

  useEffect(() => {
    if (metrixUserId) {
      lastMetrixUserId.current = metrixUserId;
      authorizeMetrixUser(authProfile, activeProfileId);
      return;
    }

    if (lastMetrixUserId.current) {
      lastMetrixUserId.current = null;
      deauthorizeMetrixUser();
    }
  }, [activeProfileId, authProfile, metrixUserId]);

  useEffect(() => {
    flushContentImpressions();
  }, [pathname, searchParams]);

  useEffect(() => {
    if (isAdminPreview) return;

    const queryString = searchParams.toString();
    const pagePath = queryString ? `${pathname}?${queryString}` : pathname;

    trackGtagEvent("screen_view", {
      screen_name: getScreenNameFromPath(pathname),
      page_path: pagePath,
      page_location: typeof window !== "undefined" ? window.location.href : pagePath,
    });

    if (typeof navigator !== "undefined" && isTvUserAgent(navigator.userAgent)) {
      trackGtagEvent("tv_navigation", {
        screen_name: getScreenNameFromPath(pathname),
        page_path: pagePath,
      });
    }

    if (!hasTrackedInitialPageView.current) {
      hasTrackedInitialPageView.current = true;
      return;
    }

    if (!isProduction || typeof window.gtag !== "function") {
      return;
    }

    window.gtag("config", GOOGLE_ANALYTICS_ID, {
      page_path: pagePath,
    });
  }, [isAdminPreview, pathname, searchParams]);

  return null;
};

export default AnalyticsClient;
